import { RingCentralIntegration } from "@/schema/ring-central-integration"
import { RingCentralOAuthGrant } from "@/schema/ring-central-oauth-grant"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

interface AuthUriResponse {
  auth_uri: string
}

export const RING_CENTRAL_INTEGRATIONS_QUERY_KEY = ["ring-central-integrations"]
export const RING_CENTRAL_GRANTS_QUERY_KEY = ["ring-central-oauth-grants"]

export function useFetchRingCentralOAuthGrants() {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<RingCentralOAuthGrant[]>({
    queryKey: [...RING_CENTRAL_GRANTS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return apiContextFetch<RingCentralOAuthGrant[]>(context, "ring_central_oauth_grants")
    },
  })
}

export function useAttachRingCentralOAuthGrant() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<RingCentralIntegration, Error, string>({
    mutationFn: async (grantId) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return apiContextFetch<RingCentralIntegration>(context, "ring_central_integrations", {
        method: "POST",
        body: JSON.stringify({ ring_central_oauth_grant_id: grantId }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_GRANTS_QUERY_KEY })
    },
  })
}

export function useFetchRingCentralIntegrations(enabled = true, pollSetup = false) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<RingCentralIntegration[]>({
    queryKey: [...RING_CENTRAL_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const data = await apiContextFetch<RingCentralIntegration[]>(context, "ring_central_integrations")
      return data
    },
    enabled,
    refetchInterval: (query) =>
      pollSetup || query.state.data?.some((integration) => integration.setup_pending) ? 2000 : false,
  })
}

export function useGetRingCentralAuthUri() {
  const getContext = useApiContext()

  return useMutation<AuthUriResponse, Error, { returnUrl?: string } | void>({
    mutationKey: ["ring-central-auth-uri"],
    mutationFn: async (params = {}) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const searchParams = new URLSearchParams()
      if (params && "returnUrl" in params && params.returnUrl) {
        searchParams.append("return_url", params.returnUrl)
      }

      const url = `ring_central_integrations/get_auth_uri${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      return apiContextFetch<AuthUriResponse>(context, url)
    },
  })
}

export function useUpdateRingCentralIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<
    RingCentralIntegration,
    Error,
    {
      id: string
      forwarding_number: string
      sender_phone_number_labels?: Record<string, string>
    }
  >({
    mutationKey: ["ring-central-integration-update"],
    mutationFn: async ({ id, ...attributes }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return apiContextFetch<RingCentralIntegration>(context, `ring_central_integrations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(attributes),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_GRANTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDisconnectRingCentralIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["ring-central-integration-disconnect"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `ring_central_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_GRANTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDeleteRingCentralIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `ring_central_integrations/${id}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_INTEGRATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: RING_CENTRAL_GRANTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}
